import GoogleAnalyticsHelper from 'helpers/GoogleAnalyticsHelper';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import usePageTracking from 'hooks/usePageTracking';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  FacebookIcon,
  FacebookShareButton,
  LineIcon,
  LineShareButton,
  TwitterShareButton,
  XIcon,
} from 'react-share';
import { useRecoilState } from 'recoil';
import { QuizContentState } from 'stores/atoms/quizContentState';
import { QuizRespondentState } from 'stores/atoms/quizRespondentState';
import { QuizPublishResultType } from 'types/quizPublishType';

/**
 * クイズ結果画面
 */
export default function QuizResult(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const navigate = useNavigate();
  const { quizCd } = useParams<{ quizCd: string }>();
  const [quizContentState, setQuizContentState] = useRecoilState(QuizContentState);
  const [quizRespondentState] = useRecoilState(QuizRespondentState);
  const [query] = useSearchParams();
  const quizHistoryCd = query.get('cd');
  const { postQuizHistory } = useApiState();
  const [correctCount, setCorrectCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [lineFlag, setLineFlag] = useState(false);
  const [lineCd, setLineCd] = useState('');
  const [resultState, setResultState] = useState<QuizPublishResultType>(null);
  const locationHref = window.location.href;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    const fetchResult = async () => {
      try {
        setResultState(await _getResult());
      } catch (e) {
        // 直接遷移時はトップ画面へ戻す
        navigate(`/quiz/${quizCd}`);
      }
    };

    fetchResult();
    setCorrectCount(
      quizRespondentState.answers.filter(v => v.answerOptionNo == v.correctOptionNo).length
    );
    setAnsweredCount(quizRespondentState.answers.length);

    // GA4に送信
    if (quizRespondentState && !quizRespondentState.preview) {
      GoogleAnalyticsHelper.event('conversion', 'branq_complete', 'クイズ回答完了');
    }
  }, []);
  // GTMトラッキング
  usePageTracking(quizContentState?.quiz?.gtmId, location.pathname, quizRespondentState?.preview);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  const onRestart = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // GA4に送信
    if (!quizRespondentState.preview) {
      GoogleAnalyticsHelper.event('conversion', 'branq_restart', 'クイズ再チャレンジ');
    }

    const url = quizRespondentState.preview
      ? `/quiz/preview/${quizCd}`
      : lineFlag
      ? `/quiz/line/${lineCd}`
      : `/quiz/${quizCd}`;
    // 元のリンク先に遷移
    navigate(url);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  const _getResult = async () => {
    let lastElementBelowScore: QuizPublishResultType = null;
    if (!quizContentState?.quiz?.results) {
      const response = await postQuizHistory({
        quizCd: quizCd,
        quizHistoryCd: quizHistoryCd,
      });
      if (!response.title) {
        navigate(`/quiz/${quizCd}/`);
      }
      lastElementBelowScore = {
        title: response.resultTitle,
        descriptionHtml: response.descriptionHtml,
        imageKey: response.imageKey,
      } as QuizPublishResultType;
      setQuizContentState({
        ...quizContentState,
        quiz: {
          ...quizContentState.quiz,
          title: response.title,
        },
        company: {
          ...quizContentState.company,
          copyright: response.copyright,
        },
      });
      setCorrectCount(response.correctCount);
      setAnsweredCount(response.answeredCount);
      setLineFlag(response.lineFlag);
      setLineCd(response.lineCd);
      // bodyのクラスを設定
      document.body.classList.add(AppHelper.getDesignClassName(response.quizTemplateId || 1));
    } else {
      // scoreThresholdで昇順にソート
      const sortedResults = [...quizContentState.quiz.results].sort(
        (a, b) => a.scoreThreshold - b.scoreThreshold
      );

      // 正解数を取得
      const correctCount = quizRespondentState.answers.filter(
        v => v.answerOptionNo == v.correctOptionNo
      ).length;
      // 条件に合う最後の要素を探す
      for (const result of sortedResults) {
        if (result.scoreThreshold <= correctCount) {
          lastElementBelowScore = result;
        } else {
          break;
        }
      }
      setLineFlag(quizContentState.quiz.lineFlag);
      setLineCd(quizContentState.quiz.lineCd);
    }

    return lastElementBelowScore;
  };

  const _getShareContent = () => {
    return `【${quizContentState.quiz.title}】
正解数　${correctCount}/${answeredCount}問　でした。
あなたも「${quizContentState.quiz.title}」にチャレンジしませんか？
`;
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {resultState && (
        <main>
          <section className="section-common-quiz section-result">
            <div className="container px-0">
              <div className="section-result-header">
                <h1 className="title-header">あなたの結果</h1>
              </div>
            </div>
            <div className="container">
              <div className="card card-quiz">
                <div className="card-body">
                  <h2 className="title-md">{quizContentState.quiz.title}</h2>

                  <div className="box-result text-center">
                    <p className="box-title">
                      正解数
                      <span className="number">
                        <strong className="number-result me-2">{correctCount}</strong>
                        <span className="number-total">{answeredCount}</span>
                      </span>
                    </p>
                    {quizRespondentState.respondentForm?.mailAddress &&
                      !quizContentState.quiz?.lineFlag && (
                        <>詳しい解説付きレポートをメールでお送りしました。</>
                      )}
                  </div>
                  {/* 見出し */}
                  <h3 className="title-md">{resultState.title}</h3>
                  {/* 回答結果のメイン画像 */}
                  {resultState.imageKey && (
                    <img src={AppHelper.getImageUrl(resultState.imageKey)} className="card-img" />
                  )}
                  {/* 本文 */}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: resultState.descriptionHtml,
                    }}
                  />

                  <ul className="list-unstyled list-button list-button-md">
                    <li className="text-center">
                      <TwitterShareButton
                        className="me-2"
                        url={locationHref}
                        title={_getShareContent()}
                        hashtags={[quizContentState.quiz.title, 'branq']}
                      >
                        <XIcon size={64} round={true} />
                      </TwitterShareButton>
                      <LineShareButton
                        className="me-2"
                        url={locationHref}
                        title={_getShareContent()}
                      >
                        <LineIcon size={64} round={true} />
                      </LineShareButton>
                      <FacebookShareButton url={locationHref} hashtag={quizContentState.quiz.title}>
                        <FacebookIcon size={64} round={true} />
                      </FacebookShareButton>
                    </li>

                    <li>
                      <span className="ql-button">
                        <a onClick={onRestart}>もう一度チャレンジ</a>
                      </span>
                    </li>
                  </ul>

                  <p className="copyright">©{quizContentState.company.copyright}</p>
                </div>
              </div>
            </div>
          </section>
        </main>
      )}
    </>
  );
}
